import {isNumber} from 'lodash-es';
import {assert} from './assert';

/**
 * Return a string in Title Case.
 */
export function getTitleCase(string: string | null): string {
   if (string == null) {
      return '';
   }

   return string
      .toLowerCase()
      .split(' ')
      .map((word) => {
         // A word starting with '(' capitalizes its second character
         if (word.startsWith('(') && word.length > 1) {
            return word.charAt(0) + word.charAt(1).toUpperCase() + word.slice(2);
         }

         return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
}

/**
 * Return true if the string is "empty" meaning null/undefined, or empty string or spaces.
 */
export function isEmptyString(string: string | null | undefined): string is null | undefined | '' {
   return string == null || string.trim() === '';
}

/**
 * Return the number as E.164 (+<country code><digits>); throws when the result
 * is not a valid US number.
 */
export function formatPhoneNumberToE164(phoneNumber: string, countryCode: string = '1'): string {
   const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');

   const e164PhoneNumber = `+${countryCode}${cleanedPhoneNumber}`;

   assert(isValidE164PhoneNumber(e164PhoneNumber), `Invalid phone number: ${phoneNumber}`);

   return e164PhoneNumber;
}

/**
 * Format a phone number as (123) 456-7890 when it is US E.164 or has 10 digits
 * after stripping non-digits; otherwise return it unchanged. Null in, null out.
 */
export function humanPhoneNumber(phoneNumber: string | null): string | null {
   if (phoneNumber == null) {
      return null;
   }
   if (isValidE164PhoneNumber(phoneNumber)) {
      return `(${phoneNumber.slice(2, 5)}) ${phoneNumber.slice(5, 8)}-${phoneNumber.slice(8)}`;
   } else {
      const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
      if (cleanedPhoneNumber.length === 10) {
         return `(${cleanedPhoneNumber.slice(0, 3)}) ${cleanedPhoneNumber.slice(3, 6)}-${cleanedPhoneNumber.slice(6)}`;
      }
      return phoneNumber;
   }
}

/** True for a US E.164 number: +1 followed by 10 digits. */
export function isValidE164PhoneNumber(phone: string): boolean {
   return /^\+1\d{10}$/.test(phone);
}

/**
 * Return the uppercased first letters of a name's first and last words, for avatars.
 * A one-word name gives one letter; a blank name gives '?'.
 */
export function getNameInitials(name: string): string {
   const words = name
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
   if (words.length === 0) {
      return '?';
   }

   const first = words[0].charAt(0);
   const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
   return `${first}${last}`.toUpperCase();
}

/**
 * Parse a string as an integer; numbers pass through. Null, undefined or
 * unparsable input returns `def`.
 */
export function buildInt(
   value: number | string | null | undefined,
   def: number | null = null,
): number | null {
   if (value == null) {
      return def;
   }
   if (isNumber(value)) {
      return value;
   }
   const val = Number.parseInt(value);
   return isNaN(val) ? def : val;
}

/**
 * Return a deterministic non-negative integer hash of a string, stable across
 * sessions and platforms. Not for security; collisions are expected.
 */
export function hashString(value: string): number {
   let hash = 0;
   for (let i = 0; i < value.length; i++) {
      // Modulo keeps the accumulator in safe-integer range
      hash = (hash * 31 + value.charCodeAt(i)) % 1_000_000_007;
   }
   return hash;
}

/** Format a byte count as a human-readable size with up to two decimals ("1.5 MB"). */
export function humanFileSize(bytes: number): string {
   if (bytes === 0) {
      return '0 Bytes';
   }

   const k = 1024;
   const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

   const i = Math.floor(Math.log(bytes) / Math.log(k));

   return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
